import { colors, layout } from "./index";

/**
 * Scale a property value between min and max of the width of the
 * viewport until it is above or below the minWidth and maxWidth
 * values.  The values must be unitless.
 * https://github.com/BKWLD/stylus-library/blob/master/lib/utils.styl#L24
 */
export const fluid = (
    property,
    {
        max,
        min = null,
        maxWidth = layout.siteMaxWidth,
        minWidth = layout.fluidMinWidth
    }
) => {
    // The multiplier when no "min" size is set
    const autoMinFactor = 0.7;
    const minSizeExpression = min === null ? `${max} * ${autoMinFactor}` : min;

    // Create empty styles object
    const styles = {};

    // Return the calc expression that sets a scaling value
    styles[property] = `calc(
        ${minSizeExpression} * 1px
        + (${max} - ${minSizeExpression})
        / (${maxWidth} - ${minWidth})
        * (100vw - ${minWidth} * 1px)
    )`;

    // Make breakpoints that cap scaling
    const maxBreak = `@media (min-width: ${maxWidth}px)`,
        minBreak = `@media (max-width: ${minWidth}px)`;

    // Cap it to max size when large
    styles[maxBreak] = { [property]: `calc(${max} * 1px)` };

    // Cap it to min size when small
    styles[minBreak] = { [property]: `calc(${minSizeExpression} * 1px)` };

    // Return the styles
    return styles;
};

/**
 * Aspect Min Height layout pattern
 * Maintain at least the aspect ratio of a container's child,
 * expanding height to meet content as needed. Defaults to 16:9
 *
 * on child styles:
 * `...aspectMinHeight.child,`
 *
 * on parent style, call the function with your aspect:
 * `...aspectMinHeight.parent(16/9)`
 */
const DEFAULT_ASPECT = 1.7777777777777777; // 16:9

export const aspectMinHeight = {
    parent: (aspect = DEFAULT_ASPECT) => {
        return {
            fontSize: 0,
            ":before": {
                content: '" "',
                display: "inline-block",
                verticalAlign: "middle",
                height: "100%",
                paddingTop: `calc((1 / ${aspect}) * 100%)`
            }
        };
    },
    parentReset: {
        ":before": {
            content: "none"
        }
    },
    child: {
        display: "inline-block",
        verticalAlign: "middle",
        width: "100%",
        position: "relative",
        fontSize: "1rem"
    }
};

/**
 * Fill the parent container using absolute positioning
 */
export const expand = background => ({
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    background: background
});

/**
 * Act like a button
 * https://github.com/BKWLD/stylus-library/blob/master/lib/input.styl#L4
 */
export const button = {
    // Show pressable cursor
    cursor: "pointer",

    // Prevent text selection
    userSelect: "none",

    // Disable double-tap to zoom
    touchAction: "manipulation"
};

/**
 * Show accessible focus outline for keyboard users.
 *
 * To hide for non-keyboard users, add the 'focus-outline-keyboard-only'
 * class to the styled component. The class removes the outline
 * when FocusVisibleManager detects a non-keyboard user.
 *
 * Use this with `extend: [focusOutline, ...]` to allow for additional
 * focus styles to also be set.
 */
export const focusOutline = {
    ":focus": {
        outlineWidth: 3,
        outlineStyle: "solid",
        outlineColor: colors.adaUiGrey,
        outlineOffset: 4
    }
};

/**
 * Apply absolute max-width
 */
export const absoluteMaxWidth = {
    maxWidth: layout.absoluteMaxWidth,
    marginH: "auto"
};

/**
 * Clear margins on first and last child, like for children of rich text fields
 */
export const clearEdgeChildMargins = {
    "> *": {
        marginBottom: 0,
        ":first-child": {
            marginTop: 0
        }
    }
};

/**
 * Hide elements in an accessible manner
 */

export const screenReaderText = {
    position: "absolute",
    margin: -1,
    border: 0,
    padding: 0,
    width: 1,
    height: 1,
    overflow: "hidden",
    clip: "rect(0, 0, 0, 0)"
};
